import { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const API_BASE = `${import.meta.env.BASE_URL}api`;

interface BannerData {
  id: number;
  imageUrl: string;
  destinationUrl: string;
  altText: string | null;
}

interface BannerAdProps {
  placement?: string;
}

export function BannerAd({ placement = 'global' }: BannerAdProps) {
  const [banner, setBanner] = useState<BannerData | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const key = `banner_dismissed_${placement}`;
    if (sessionStorage.getItem(key) === 'true') {
      setDismissed(true);
      return;
    }

    fetch(`${API_BASE}/banners/active?placement=${placement}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (d) setBanner(d);
      })
      .catch(() => {});
  }, [placement]);

  useEffect(() => {
    if (banner && banner.id) {
      fetch(`${API_BASE}/banners/${banner.id}/impression`, { method: 'POST' }).catch(() => {});
    }
  }, [banner?.id]);

  if (!banner || dismissed) return null;

  const handleClick = () => {
    fetch(`${API_BASE}/banners/${banner.id}/click`, { method: 'POST' }).catch(() => {});
    window.open(banner.destinationUrl, '_blank', 'noopener,noreferrer');
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDismissed(true);
    sessionStorage.setItem(`banner_dismissed_${placement}`, 'true');
  };

  return (
    <div className="fixed bottom-[72px] left-0 right-0 z-40 flex justify-center px-2">
      <div className="w-full max-w-md relative">
        <div
          onClick={handleClick}
          className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg overflow-hidden cursor-pointer hover:border-white/20 transition-colors flex items-center justify-center"
          style={{ height: '50px' }}
        >
          <img
            src={banner.imageUrl}
            alt={banner.altText || 'Advertisement'}
            className="h-full w-full object-contain"
          />
        </div>
        <button
          onClick={handleDismiss}
          className="absolute -top-2 -right-1 w-5 h-5 bg-black/80 border border-white/20 rounded-full flex items-center justify-center text-gray-400 hover:text-white transition-colors"
          aria-label="Dismiss banner"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}
